import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { ApiResponse } from '../types/index';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-tt.vitrioll.com';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle common errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors (unauthorized)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Clear token and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          
          // Redirect to landing page
          window.location.href = '/';
          
          return Promise.reject(error);
        }

        // Handle network errors
        if (!error.response) {
          throw new Error('Network error. Please check your connection.');
        }

        // Handle other HTTP errors
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'An unexpected error occurred';
        throw new Error(errorMessage);
      }
    );
  }

  // Generic GET request
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<any> {
    try {
      const response = await this.client.get<any>(endpoint, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic POST request
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post<ApiResponse<T>>(endpoint, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic PUT request
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put<ApiResponse<T>>(endpoint, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(endpoint);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors consistently
  private handleError(error: any): Error {
    if (error instanceof Error) {
      return error;
    }
    
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    
    if (error.response?.data?.error) {
      return new Error(error.response.data.error);
    }
    
    return new Error('An unexpected error occurred');
  }

  // Set auth token
  setAuthToken(token: string) {
    localStorage.setItem('authToken', token);
  }


  // Clear auth token
  clearAuthToken() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  // Get current auth token
  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Twitter Followers API methods
  async checkFollowerStatus(usernames: string[]): Promise<any> {
    return this.post(`/twitter-followers/check-status`, usernames);
  }

  async fetchFollowers(username: string): Promise<any> {
    return this.post(`/twitter-followers/fetch`, { username });
  }

  async getFollowerProgress(username: string): Promise<any> {
    return this.get(`/twitter-followers/progress/${username}`);
  }

  async getFollowers(username: string, page: number = 1, limit: number = 50): Promise<any> {
    return this.get(`/twitter-followers/${username}`, { page, limit });
  }

  async getMultipleFollowers(
    usernames: string[],
    page: number = 1,
    limitPerUser: number = 50,
    onlyCommon: boolean = false
  ): Promise<any> {
    return this.post(`/twitter-followers/multiple`, {
      usernames,
      page,
      limit_per_user: limitPerUser,
      only_common: onlyCommon
    });
  }

  async getListFollowerStatus(listId: string): Promise<any> {
    return this.get(`/lists/${listId}/follower-status`);
  }

  // New endpoints for user posts
  async getUserPosts(username: string, page: number = 1, limit: number = 50): Promise<any> {
    return this.get(`/users/${username}/posts`, { page, limit });
  }

  async getMultipleUsersPosts(usernames: string[], page: number = 1, limit: number = 50): Promise<any> {
    return this.post(`/users/posts?page=${page}&limit=${limit}`, usernames);
  }
}

export const apiService = new ApiService();
export default apiService;